import { gsap } from "gsap";
import parse from "html-react-parser";
import React, { useEffect, useRef } from "react";
import { Col, Container, Row } from "react-bootstrap";
import useOnScreen from "./use-on-screen";

const Quote = ({ body = "", colour = "#000" }) => {
	const quoteRef = useRef(null);
	const isVisible = useOnScreen(quoteRef, "-50px");

	/**
	 * 1) Sanitize the HTML in the browser.
	 */
	let sanitizedHtml = body;
	if (typeof window !== "undefined") {
		const DOMPurify = require("dompurify");
		sanitizedHtml = DOMPurify.sanitize(sanitizedHtml);
	}

	/**
	 * 2) Use html-react-parser, splitting text nodes into word-wrapping spans.
	 */
	const splittedContent = parse(sanitizedHtml, {
		replace: (node) => {
			if (node.type === "text") {
				const textContent = node.data || "";
				const words = textContent.split(/\s+/);

				return (
					<>
						{words.map((word, i) => {
							// If it's just whitespace, return a space
							if (!word.trim()) return " ";

							return (
								// Outer container: inline-block, overflow hidden
								// so the word can hide below and slide up.
								<span
									key={i}
									style={{
										display: "inline-block",
										position: "relative",
										overflow: "hidden",
										marginRight: "6px",
									}}
								>
									{/* 
                    Inner span: We'll set transform: translateY(100%) so it
                    starts below the container, then animates to 0.
                  */}
									<span
										className="wordText"
										style={{
											display: "inline-block",
											position: "relative",
											transform: "translateY(100%)",
										}}
									>
										{word}
									</span>{" "}
								</span>
							);
						})}
					</>
				);
			}
			return undefined;
		},
	});

	/**
	 * 3) Animate once the component is visible.
	 */
	useEffect(() => {
		console.log("isVisible:", isVisible); // Debug check
		if (!isVisible) return;

		const wordTexts = quoteRef.current?.querySelectorAll(".wordText");
		if (!wordTexts || wordTexts.length === 0) return;

		// We'll re-set them at runtime to ensure they're placed below
		gsap.set(wordTexts, { y: "100%" });

		// Animate them up
		gsap.to(wordTexts, {
			y: "0%",
			duration: 0.6,
			stagger: 0.08,
			ease: "power3.out",
			delay: 0.2,
		});
	}, [isVisible, body]);

	return (
		<section className="py-5 py-lg-7">
			<Container>
				<Row className="justify-content-center">
					<Col className="text-center text-md-start">
						{/* IntersectionObserver watches this ref */}
						<div ref={quoteRef} style={{ color: colour }}>
							{splittedContent}
						</div>
					</Col>
				</Row>
			</Container>
		</section>
	);
};

export default Quote;
